import { useRouter } from 'next/router'
import Head from 'next/head'
import Script from 'next/script'
import { NextSeo } from 'next-seo'
import React, { FC, useEffect, useState } from 'react'
import type { Product } from '@commerce/types/product'
import HeroPDP from '@components/ui/HeroPDP'
import ProductPageSections from './ProductPageSections'
import { urlFor } from '@lib/sanity'

interface ProductViewProps {
  product: Product
  productData?: any
  relatedProducts?: Product[]
  messages: any
  showPromo?: boolean
}

const ProductView: FC<ProductViewProps> = ({ product, productData, messages = {} }) => {
  const router = useRouter()
  const locale = router.locale || 'de'
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.myremi.com'
  const canonicalUrl = `${baseUrl}/${locale}/products/${product?.slug ?? ''}`
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    document.body.className = 'bright_header'
    setLoading(false)

    const pushElevarViewItem = () => {
      // @ts-ignore
      if (typeof window !== 'undefined' && window.ElevarDataLayer && !window.ElevarDataLayer.find((o: any) => o.event === 'dl_view_item')) {
        // @ts-ignore
        window.ElevarDataLayer.push({
          event: 'dl_view_item',
          user_properties: { user_consent: '', visitor_type: 'guest' },
          ecommerce: {
            currencyCode: 'EUR',
            detail: {
              actionField: { list: location.pathname, action: 'detail' },
              products: [{
                id: product?.id,
                name: product?.name,
                category: '',
                brand: 'Remi Health',
                list: 'Product List',
                variant: product?.name,
                price: (product?.variants[0]?.price)?.toString(),
                product_id: product?.id,
                variant_id: product?.variants[0]?.id,
                compare_at_price: '0.0',
                image: product?.images[0]?.url
              }]
            }
          }
        })
      }
    }

    // Defer analytics to idle so it doesn't add to main-thread execution time
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(pushElevarViewItem, { timeout: 2000 })
    } else {
      setTimeout(pushElevarViewItem, 300)
    }

    return () => { document.body.className = '' }
  }, [product, productData])

  const lcpImageMobile = productData?.teaserImages?.itemsMobile?.[0] || productData?.teaserImages?.items?.[0]
  const lcpImageDesktop = productData?.teaserImages?.items?.[0]
  const lcpUrlMobile = lcpImageMobile ? urlFor(lcpImageMobile?.teaserImage).url() : null
  const lcpUrlDesktop = lcpImageDesktop ? urlFor(lcpImageDesktop?.teaserImage).url() : null

  return (
    <>
      {(lcpUrlMobile || lcpUrlDesktop) && (
        <Head>
          {lcpUrlMobile && (
            <link
              rel="preload"
              as="image"
              href={lcpUrlMobile}
              // @ts-ignore: fetchpriority (lowercase) is the correct HTML attribute; fetchPriority causes a React warning
              fetchpriority="high"
              media="(max-width: 1024px)"
            />
          )}
          {lcpUrlDesktop && (
            <link
              rel="preload"
              as="image"
              href={lcpUrlDesktop}
              // @ts-ignore: fetchpriority (lowercase) is the correct HTML attribute; fetchPriority causes a React warning
              fetchpriority="high"
              media="(min-width: 1025px)"
            />
          )}
        </Head>
      )}
      <HeroPDP
        headline={productData?.title}
        subhead={productData?.subtitle}
        description={productData?.description}
        productImages={productData?.teaserImages}
        bestseller={productData?.bestseller}
        recommendation={productData?.recommendation}
        product={product}
        className={'product'}
        discounts={productData?.discounts?.items}
        upsellingItems={productData?.upsellingItems}
        subscriptionConfig={productData?.subscriptionConfig}
        usps={productData?.uspSliderProduct?.items}
        prodInfo={productData?.productInfoSidebar}
        prodInfo2={productData?.productInfoSidebar2}
        howTo={productData?.howToSidebar}
        chips={productData?.chipsList?.items}
        reviewers={productData?.reviewers}
        rating={productData?.rating}
        colorDark={productData?.color_dark}
        colorLight={productData?.color_light}
        priceBanner={productData?.priceBanner}
      />

      {!loading && <ProductPageSections productData={productData} />}

      <NextSeo
        title={productData?.title}
        description={productData?.description}
        canonical={canonicalUrl}
        openGraph={{
          type: 'product',
          title: productData?.title,
          description: productData?.description,
          url: canonicalUrl,
          images: [
            {
              url: product?.images?.[0]?.url ?? '',
              width: 800,
              height: 600,
              alt: productData?.title ?? ''
            }
          ]
        }}
        languageAlternates={[
          { hrefLang: 'de', href: `${baseUrl}/de/products/${product?.slug ?? ''}` },
          { hrefLang: 'en', href: `${baseUrl}/en/products/${product?.slug ?? ''}` },
          { hrefLang: 'x-default', href: `${baseUrl}/de/products/${product?.slug ?? ''}` },
        ]}
        additionalMetaTags={[
          {
            property: 'product:price:amount',
            content: product?.variants?.[0]?.price?.toString() ?? ''
          },
          {
            property: 'product:price:currency',
            content: 'EUR'
          }
        ]}
      />
      {product && productData && (
        <Script
          id="product-jsonld"
          type="application/ld+json"
          strategy="afterInteractive"
        >
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: productData.title ?? product.name,
            description: productData.description ?? product.description,
            image: product?.images?.[0]?.url,
            sku: product?.sku ?? (product?.variants?.[0] as { sku?: string } | undefined)?.sku,
            brand: { '@type': 'Brand', name: 'Remi Health' },
            offers: {
              '@type': 'Offer',
              price: product?.variants?.[0]?.price,
              priceCurrency: 'EUR',
              availability: 'https://schema.org/InStock'
            },
            ...(productData?.rating != null && {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: productData.rating,
                reviewCount: productData.reviewers ?? 0
              }
            })
          })}
        </Script>
      )}
    </>
  )
}

export default ProductView
